/**
 * Лічильник подання біля його назви (LST-15; views.md, розд. 7): `POST /api/v1/views/counts`
 * з `{ ids }` → `[{ id, count, capped }]`. Рахує сервер тим самим предикатом прав, що й список
 * (SEC-049), точно до межі: до 1 000 — число, більше — «1 000+», межа часу 1 с — `count: null`
 * («—», рішення AG-49). Оцінок планувальника немає.
 *
 * Запит іде окремо від першої порції й паралельно з нею: рядки його не чекають. На старті застосунку
 * — після межі першого екрана (perf/first-screen.ts, бюджет запитів першого екрана). Ендпоінт дає
 * E4a-03 (8ae40447); поки його немає (404), лічильника не видно і помилки теж (рішення PM 20.10).
 * Кеш — 60 с, свіжість — події SSE (events.ts інвалідовує лічильники сутності).
 *
 * Лічильники, що попросили в одній мікрозадачі (кілька подань на екрані, повтор після події),
 * їдуть одним пакетом до 10 id; пакети — через смугу лічильників (`counts-lane.ts`): по одному,
 * без переривання відправленого, з тихим повтором на `429` (аудит № 21). Збій — не `null`, а
 * помилка запиту: попереднє значення лишається на екрані, а не зникає на 60 с. Відмова тримається
 * 60 с (`VIEW_COUNT_FAIL_MS`): повторне монтування списку чи дошки в цей час не шле запит знову.
 * Не тримаються `401` і обрив мережі; явна інвалідація лічильника забуває відмову
 * (`forgetViewCountFailure`).
 */
import { queryOptions } from '@tanstack/react-query';
import { ApiProblem, api } from './api.ts';
import { countsLane } from './counts-lane.ts';
import { queryPolicy } from './query-policy.ts';

export interface ViewCount {
  id: string;
  count: number | null;
  capped: boolean;
}

/** Межа точного підрахунку (LST-15): більше — «1 000+». */
export const COUNT_CAP = 1000;

export const viewCountKey = (entity: string, viewId: string) =>
  ['p', 'count', entity, viewId] as const;
export const viewCountsKey = (entity: string) => ['p', 'count', entity] as const;

/** Відповідь за контрактом — масив; обгортку `{ items }` теж приймаємо. */
function pick(body: unknown, id: string): ViewCount | null {
  const items = Array.isArray(body)
    ? body
    : Array.isArray((body as { items?: unknown } | null)?.items)
      ? (body as { items: unknown[] }).items
      : [];
  const hit = items.find((x) => (x as { id?: unknown } | null)?.id === id) as
    | Partial<ViewCount>
    | undefined;
  if (!hit) return null;
  const count = typeof hit.count === 'number' && Number.isFinite(hit.count) ? hit.count : null;
  return { id, count, capped: hit.capped === true };
}

/** Id в одному пакеті — межа контракту `POST /views/counts` (до 10). */
export const VIEW_COUNTS_MAX = 10;

interface CountWaiter {
  resolve: (value: unknown) => void;
  reject: (error: unknown) => void;
  signal: AbortSignal | undefined;
}

let waiting = new Map<string, CountWaiter[]>();

/** Скільки тримати відмову лічильника: монтування в цей час не шле запит знову (як свіжість). */
export const VIEW_COUNT_FAIL_MS = 60_000;
/** Остання відмова подання: коли (`performance.now()`) і з чим. */
let failures = new Map<string, { at: number; error: unknown }>();
let scheduled = false;

/** Тіло відповіді пакета, що містить `id`: глядачі одного id в мікрозадачі — один запит. */
function loadCounts(id: string, signal?: AbortSignal): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const list = waiting.get(id) ?? [];
    list.push({ resolve, reject, signal });
    waiting.set(id, list);
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(flushCounts);
  });
}

function flushCounts() {
  scheduled = false;
  // Хто скасував до відправлення — без запиту.
  const entries = [...waiting]
    .map(([id, list]) => [id, list.filter((w) => !w.signal?.aborted)] as const)
    .filter(([, list]) => list.length > 0);
  waiting = new Map();
  for (let i = 0; i < entries.length; i += VIEW_COUNTS_MAX) {
    const chunk = entries.slice(i, i + VIEW_COUNTS_MAX);
    const ids = chunk.map(([id]) => id).sort();
    const waiters = chunk.flatMap(([, list]) => list);
    // Пакет скасовують, лише коли скасували всі його глядачі (і лише до відправлення: смуга).
    const controller = new AbortController();
    const onAbort = () => {
      if (waiters.every((w) => w.signal?.aborted)) controller.abort();
    };
    for (const w of waiters) w.signal?.addEventListener('abort', onAbort, { once: true });
    countsLane
      .run(
        `views:${ids.join(',')}`,
        (sent) =>
          api<unknown>('POST', '/api/v1/views/counts', { body: { ids }, signal: sent, read: true }),
        controller.signal,
      )
      .then(
        (body) => {
          for (const w of waiters) w.resolve(body);
        },
        (error: unknown) => {
          for (const w of waiters) w.reject(error);
        },
      )
      .finally(() => {
        for (const w of waiters) w.signal?.removeEventListener('abort', onAbort);
      });
  }
}

/**
 * Відмову не тримаємо, коли вона не про лічильник: `401` (після входу знову — інша сесія) і обрив
 * мережі (зв'язок повернувся — запит має піти, а не чекати 60 с).
 */
const remembered = (error: unknown) =>
  !(error instanceof ApiProblem && (error.status === 401 || error.status === 0));

/**
 * Забути відмову подання: явна інвалідація лічильника (збереження подання з іншою умовою, нова
 * редакція) мусить надіслати запит, а не повернути стару відмову з-під іншої умови.
 */
export function forgetViewCountFailure(viewId: string): void {
  failures.delete(viewId);
}

/** Забути глядачів пакета (стан модуля між тестами, test/setup.ts). */
export function resetViewCountsForTests() {
  waiting = new Map();
  failures = new Map();
  scheduled = false;
}

/**
 * Лічильник одного подання. `null` — показувати нічого: ендпоінта ще немає (`404`) чи чужа форма.
 * Інший збій — помилка запиту без повторів: попереднє значення лишається (`useAreaState`), а
 * помилка не доходить до екрана й не йде в журнал збоїв (`quiet`): лічильник — орієнтир.
 */
export const viewCountQuery = (entity: string, viewId: string) =>
  queryOptions({
    queryKey: viewCountKey(entity, viewId),
    queryFn: async ({ signal }): Promise<ViewCount | null> => {
      const failed = failures.get(viewId);
      if (failed && performance.now() - failed.at < VIEW_COUNT_FAIL_MS) throw failed.error;
      try {
        const count = pick(await loadCounts(viewId, signal), viewId);
        failures.delete(viewId);
        return count;
      } catch (error) {
        if (error instanceof ApiProblem && error.status === 404) return null;
        if (!signal?.aborted && remembered(error))
          failures.set(viewId, { at: performance.now(), error });
        throw error;
      }
    },
    ...queryPolicy.counts,
    retry: false,
    meta: { quiet: true },
  });

/** Текст лічильника: «86», «1 000+», «—» (межа часу); `null` — нічого не показувати. */
export function countLabel(
  count: Pick<ViewCount, 'count' | 'capped'> | null | undefined,
): string | null {
  if (!count) return null;
  if (count.count === null) return '—';
  if (count.capped || count.count > COUNT_CAP) return `${COUNT_CAP.toLocaleString('uk-UA')}+`;
  return count.count.toLocaleString('uk-UA');
}

/** Точна кількість для `aria-rowcount`: лише коли число не обмежене межею. */
export const exactCount = (count: ViewCount | null | undefined): number | undefined =>
  count && count.count !== null && !count.capped ? count.count : undefined;
